/* Shared by both clients and Cloud Functions. Stored recurrence dates and
 * exception keys use UTC; display zones do not change the saved cadence. */
import {DateTime} from 'luxon';
const DAY = 86400000;

function availabilityWeek(target, timeZone = 'UTC') {
  const local = typeof target === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(target)
    ? DateTime.fromISO(target, {zone: timeZone})
    : DateTime.fromJSDate(new Date(target), {zone: 'utc'}).setZone(timeZone);
  if (!local.isValid) throw new Error('Invalid availability week or time zone');
  const start = local.startOf('week');
  return {weekKey: start.toISODate(), start: start.toUTC().toJSDate(),
    end: start.plus({days: 7}).toUTC().toJSDate()};
}

// Half-open window [start, end): touching midnight is not an overlap.
function expandAvailability(records, windowStart, windowEnd) {
  const ws = +new Date(windowStart), we = +new Date(windowEnd);
  if (!Number.isFinite(ws) || !Number.isFinite(we) || we <= ws) return [];
  const out = [];
  for (const record of records || []) {
    if (record.unavailableAllWeek) continue;
    const base = Date.parse(record.startUTC), end = Date.parse(record.endUTC);
    const duration = end - base, repeat = record.repeat || 'none';
    if (!Number.isFinite(base) || !Number.isFinite(end) || duration <= 0) continue;
    const id = record.id || `${record.userId || 'u'}_${record.startUTC}_${record.endUTC}_${repeat}`;
    const exceptions = new Set(Array.isArray(record.exceptions) ? record.exceptions : []);
    const overrides = record.overrides && typeof record.overrides === 'object' ? record.overrides : {};
    const until = record.repeatEndDate ? Date.parse(`${record.repeatEndDate}T23:59:59.999Z`) : Infinity;
    const push = (original, override) => {
      const key = new Date(original).toISOString().slice(0, 10);
      if (original > until || exceptions.has(key)) return;
      const startMs = override ? Date.parse(override.startUTC) : original;
      const endMs = override ? Date.parse(override.endUTC) : original + duration;
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs || startMs >= we || endMs <= ws) return;
      out.push({...record, ...(override || {}), id,
        startUTC: new Date(startMs).toISOString(), endUTC: new Date(endMs).toISOString(),
        isRecurringInstance: repeat !== 'none', instanceDate: key,
        ...(repeat !== 'none' ? {seriesStartUTC: record.startUTC, seriesEndUTC: record.endUTC} : {})});
    };
    if (repeat === 'none') {
      // A nonrecurring record does not use series exceptions/overrides.
      if (base < we && end > ws) out.push({...record, id, isRecurringInstance: false});
      continue;
    }
    const step = (repeat === 'weekly' ? 7 : repeat === 'biweekly' ? 14 : 0) * DAY;
    if (!step || Number.isNaN(until)) continue;
    // Jump directly to the window, including overnight/multiday intervals.
    const first = Math.max(0, Math.floor((ws - duration - base) / step) + 1);
    for (let original = base + first * step; original < we && original <= until; original += step) {
      const key = new Date(original).toISOString().slice(0, 10);
      // Overrides are handled separately, including those moved into this week.
      if (!Object.prototype.hasOwnProperty.call(overrides, key)) push(original);
    }
    for (const [key, override] of Object.entries(overrides)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key) || !override) continue;
      const original = Date.parse(`${key}T${new Date(base).toISOString().slice(11)}`);
      if (original >= base && (original - base) % step === 0) push(original, override);
    }
  }
  return out;
}

function availabilitySubmission(records, userIds, weekKey, timeZone = 'UTC') {
  const week = availabilityWeek(weekKey, timeZone);
  const unavailable = new Set((records || []).filter(record => record.unavailableAllWeek === true &&
    record.submissionWeekKey === week.weekKey).map(record => record.userId).filter(Boolean));
  const instances = expandAvailability((records || []).filter(record => !unavailable.has(record.userId)), week.start, week.end);
  const submitted = new Set([...unavailable, ...instances.map(record => record.userId).filter(Boolean)]);
  const expected = [...new Set(userIds || [])];
  return {week, unavailable, instances, submitted,
    complete: expected.length > 0 && expected.every(uid => submitted.has(uid))};
}

export {availabilityWeek, expandAvailability, availabilitySubmission};
